import { useCallback } from 'react';
import { Platform } from 'react-native';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  selectBackofficeAccessMode,
  selectBackofficeUnlockTtlMs,
  selectAllowDeviceCredentialsFallback,
  selectEmployeeRequireBiometricsOnForeground,
} from '../../redux/SettingsSlice';
import {
  selectBackofficeLastUnlockAtMs,
  selectBiometricInFlight,
  selectSensorInfo,
  setBackofficeLastUnlockAtMs,
  setBiometricInFlight,
  setSensorInfo,
} from '../../redux/securityRuntimeSlice';
import {
  authenticateWithOptions,
  BiometricStrength,
  isSensorAvailable,
} from '@sbaiahmed1/react-native-biometrics';
import { AppAlert } from '../../services/AppAlert';

type NavLike = { navigate: (name: any, params?: any) => void };

const isBackofficeRouteName = (name: string) => {
  if (!name) return false;
  if (name === 'BackOffice') return true;
  if (name === 'Browser') return true; // WebView backoffice
  return name.startsWith('BackOffice');
};

export function useSecureNavigate(navigation: NavLike) {
  const dispatch = useAppDispatch();

  const accessMode = useAppSelector(selectBackofficeAccessMode);
  const ttlMs = useAppSelector(selectBackofficeUnlockTtlMs);
  const lastUnlockAt = useAppSelector(selectBackofficeLastUnlockAtMs);
  const biometricInFlight = useAppSelector(selectBiometricInFlight);
  const sensor = useAppSelector(selectSensorInfo);
  const employeeRequireBiometricsOnForeground = useAppSelector(
    selectEmployeeRequireBiometricsOnForeground,
  );
  const allowDeviceCredentialsFallback = useAppSelector(
    selectAllowDeviceCredentialsFallback,
  );

  const ensureSensorAvailable = useCallback(async () => {
    // Web: treat as always available (we don't gate).
    if (Platform.OS === 'web') return { available: true, error: undefined };

    // Prefer cached info when present.
    let available = sensor?.info?.available === true;
    let error = sensor?.info?.error;

    // Ensure we have up-to-date sensor info at least once.
    if (!sensor?.checked) {
      try {
        const info = await isSensorAvailable({
          biometricStrength: BiometricStrength.Strong,
        } as any);
        dispatch(setSensorInfo(info as any));
        available = (info as any)?.available === true;
        error = (info as any)?.error;
      } catch (e: any) {
        available = false;
        error = e?.message;
        dispatch(
          setSensorInfo({
            available: false,
            biometryType: 'Unknown',
            error: e?.message,
            errorCode: e?.code || e?.errorCode,
          } as any),
        );
      }
    }

    return { available, error };
  }, [dispatch, sensor?.checked, sensor?.info?.available, sensor?.info?.error]);

  const runBiometricAuth = useCallback(async () => {
    if (Platform.OS === 'web') return true;
    if (biometricInFlight) return false;

    const { available, error } = await ensureSensorAvailable();
    // If device-credential fallback is enabled, we can still authenticate
    // even when biometrics are not enrolled/available (e.g. use PIN/pattern/password).
    if (!available && !allowDeviceCredentialsFallback) {
      AppAlert.alert(
        'Biometrics unavailable',
        error || 'Biometric authentication is not available.',
      );
      return false;
    }

    try {
      dispatch(setBiometricInFlight(true));

      const result = await authenticateWithOptions({
        title: 'Locked',
        subtitle: 'Authenticate to continue',
        allowDeviceCredentials: allowDeviceCredentialsFallback,
        disableDeviceFallback: !allowDeviceCredentialsFallback,
        biometricStrength: BiometricStrength.Strong,
      } as any);

      return result?.success === true;
    } catch {
      return false;
    } finally {
      dispatch(setBiometricInFlight(false));
    }
  }, [
    allowDeviceCredentialsFallback,
    biometricInFlight,
    dispatch,
    ensureSensorAvailable,
  ]);

  const ensureSecurityUnlocked = useCallback(async () => {
    if (Platform.OS === 'web') return true;
    console.log('ensureSecurityUnlocked', biometricInFlight);
    return await runBiometricAuth();
  }, [biometricInFlight, runBiometricAuth]);

  const ensureBackofficeUnlocked = useCallback(async () => {
    if (Platform.OS === 'web') return true;
    if (accessMode === 'autologin_only') return true;

    const now = Date.now();
    if (lastUnlockAt && now - lastUnlockAt < ttlMs) return true;
    console.log(
      'ensureBackofficeUnlocked',
      lastUnlockAt,
      now,
      ttlMs,
      biometricInFlight,
    );

    const ok = await runBiometricAuth();
    if (!ok) return false;

    dispatch(setBackofficeLastUnlockAtMs(now));
    return true;
  }, [
    accessMode,
    biometricInFlight,
    dispatch,
    lastUnlockAt,
    ttlMs,
    runBiometricAuth,
  ]);

  const secureNavigate = useCallback(
    async (routeName: string, params?: any) => {
      // Non-native platforms: no biometric gating.
      if (Platform.OS === 'web') {
        navigation.navigate(routeName as any, params);
        return true;
      }

      // Security settings are gated only when biometrics are enabled anywhere
      if (routeName === 'SecuritySettingsScreen') {
        const shouldGate =
          accessMode === 'biometrics_gate' ||
          employeeRequireBiometricsOnForeground;
        if (shouldGate) {
          const ok = await ensureSecurityUnlocked();
          if (!ok) return false;
        }
        navigation.navigate(routeName as any, params);
        return true;
      }

      if (!isBackofficeRouteName(routeName)) {
        navigation.navigate(routeName as any, params);
        console.log('secureNavigate', routeName, params);
        return true;
      }

      if (accessMode === 'autologin_only') {
        navigation.navigate(routeName as any, params);
        return true;
      }

      // biometrics_gate
      const ok = await ensureBackofficeUnlocked();
      if (!ok) return false;
      navigation.navigate(routeName as any, params);
      return true;
    },
    [
      accessMode,
      employeeRequireBiometricsOnForeground,
      ensureBackofficeUnlocked,
      ensureSecurityUnlocked,
      navigation,
    ],
  );

  return {
    secureNavigate,
    ensureBackofficeUnlocked,
    ensureSecurityUnlocked,
    ensureSensorAvailable,
    runBiometricAuth,
  } as const;
}
